import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import {
  SpaOutlined,
  TimerOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';
import Reveal from '../../atome/motion/reveal';
import {
  CardContainer,
  CarouselShell,
  CarouselWrapper,
  CustomContainer,
  Dot,
  Dots,
  GhostCta,
  HeroActions,
  HeroCopy,
  HeroMeta,
  HeroRings,
  HeroWrapper,
  ImageCard,
  NavButton,
  PrimaryCta,
  SlideCaption,
} from './hero.styled';

/** Founder-first ordering: Karen opens the stack. */
const slides = [
  { src: '/img/klarity-karen.jpg', caption: 'Karen · Founder' },
  { src: '/img/klarity-cover.jpg', caption: 'The Klarity ritual' },
  { src: '/img/klarity-karen-product.jpg', caption: 'Made & checked by hand' },
];

const MAX_VISIBILITY = 3;

const Carousel = ({ items }: { items: typeof slides }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (paused || count < 2) return;
    timerRef.current = setInterval(() => setActive((prev) => (prev + 1) % count), 3500);
  }, [count, paused]);

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoPlay]);

  const goTo = (index: number) => {
    setActive((index + count) % count);
    startAutoPlay();
  };

  return (
    <CarouselShell onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <CarouselWrapper>
        {count > 1 && (
          <NavButton direction="left" onClick={() => goTo(active - 1)} aria-label="Previous slide">
            {'<'}
          </NavButton>
        )}

        {items.map((slide, i) => {
          // shortest circular distance from the active card
          let distance = (i - active + count) % count;
          if (distance > count / 2) distance -= count;

          const offset = -distance / 3;
          const absOffset = Math.abs(distance) / 3;
          const direction = Math.sign(-distance);
          const isActive = distance === 0;

          const style: React.CSSProperties = {
            transform: `
              rotateY(${offset * 44}deg)
              scale(${1 - absOffset * 0.16})
              translateZ(${absOffset * -420}px)
              translateX(${direction * -70}px)
            `,
            filter: `blur(${absOffset * 9}px) saturate(${1 - absOffset * 0.3})`,
            opacity: Math.abs(distance) >= MAX_VISIBILITY ? 0 : 1,
            pointerEvents: isActive ? 'auto' : 'none',
            display: Math.abs(distance) > MAX_VISIBILITY ? 'none' : 'block',
          };

          return (
            <CardContainer key={slide.src} $style={style} $zIndex={1000 - Math.abs(distance)}>
              <ImageCard>
                <img
                  src={slide.src}
                  alt={slide.caption}
                  onError={(e) => {
                    e.currentTarget.src = 'https://placehold.co/600x800?text=Klarity';
                  }}
                />
              </ImageCard>
            </CardContainer>
          );
        })}

        {count > 1 && (
          <NavButton direction="right" onClick={() => goTo(active + 1)} aria-label="Next slide">
            {'>'}
          </NavButton>
        )}

        <SlideCaption key={items[active].caption}>{items[active].caption}</SlideCaption>
      </CarouselWrapper>

      {count > 1 && (
        <Dots>
          {items.map((slide, i) => (
            <Dot
              key={`dot-${slide.src}`}
              $active={i === active}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </Dots>
      )}
    </CarouselShell>
  );
};

interface HeroProps {
  /** Copy adapts to a one-product shop vs. a catalogue. */
  productCount?: number;
}

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const HeroSection = ({ productCount = 1 }: HeroProps) => {
  const isSolo = productCount <= 1;

  return (
    <HeroWrapper>
      <HeroRings aria-hidden="true" />
      <CustomContainer maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal from="up">
              <HeroCopy>
                <h1>
                  Bright eyes in <em>two minutes</em>
                </h1>
                <p>
                  {isSolo
                    ? 'One quietly obsessive formula for tired eyes — naturally derived, hand checked, and delivered to your door.'
                    : 'A small collection of naturally derived essentials for tired eyes and skin that needs a minute to itself.'}
                </p>

                <HeroActions>
                  <PrimaryCta onClick={() => scrollTo('product-section')}>
                    {isSolo ? 'Shop the signature' : 'Shop the collection'}
                  </PrimaryCta>
                  <GhostCta onClick={() => scrollTo('founder')}>Meet the founder</GhostCta>
                </HeroActions>

                <HeroMeta>
                  <span>
                    <TimerOutlined /> Visible in 2 minutes
                  </span>
                  <span>
                    <SpaOutlined /> Naturally derived
                  </span>
                  <span>
                    <VerifiedOutlined /> Small batch, hand checked
                  </span>
                </HeroMeta>
              </HeroCopy>
            </Reveal>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal from="zoom" delay={140}>
              <Carousel items={slides} />
            </Reveal>
          </Grid>
        </Grid>
      </CustomContainer>
    </HeroWrapper>
  );
};

export default HeroSection;
